import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthComplete } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { LoadingButton } from '../common/LoadingComponents';
import { validation } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';
import { generateId } from '../../utils/accessibility';

function LoginForm() {
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    password: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const [formId] = useState(() => generateId('login-form'));
  const [infoMessage] = useState(location.state?.message || null);

  const { login, isAuthenticated, error } = useAuthComplete();
  const toast = useToast();
  const navigate = useNavigate();

  // Focus appropriate input on mount
  useEffect(() => {
    if (location.state?.email && passwordInputRef.current) {
      // If email is pre-filled (from registration), focus password
      passwordInputRef.current.focus();
    } else if (emailInputRef.current) {
      // Otherwise focus email
      emailInputRef.current.focus();
    }
  }, [location.state?.email]);

  // Show info message from registration redirect
  useEffect(() => {
    if (infoMessage) {
      toast.info(infoMessage);
    }
  }, [infoMessage, toast]);

  // Debug logging
  useEffect(() => {
    console.log('LoginForm - Current error state:', error);
  }, [error]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || ROUTES.HOME;
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Don't clear general error on input change - let user see it
  };

  const validateForm = () => {
    const errors = {};
    
    const emailError = validation.validateEmail(formData.email);
    if (emailError) errors.email = emailError;
    
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await login({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
      });
      
      if (result.success) {
        toast.success('Welcome back! You have successfully signed in.');
        const from = location.state?.from?.pathname || ROUTES.HOME;
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      // Error toast will be shown via error state display
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 id={`${formId}-title`}>Welcome Back</h1>
          <p>Sign in to your QDN account</p>
        </div>

        {infoMessage && (
          <div className="info-message" role="status" aria-live="polite">
            {infoMessage}
          </div>
        )}

        {error && !infoMessage && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" noValidate aria-labelledby={`${formId}-title`}>
          <div className="form-group">
            <label htmlFor={`${formId}-email`}>Email Address</label>
            <input
              type="email"
              id={`${formId}-email`}
              name="email"
              ref={emailInputRef}
              value={formData.email}
              onChange={handleInputChange}
              className={fieldErrors.email ? 'form-input error' : 'form-input'}
              placeholder="Enter your email"
              autoComplete="email"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? `${formId}-email-error` : undefined}
            />
            {fieldErrors.email && (
              <span id={`${formId}-email-error`} className="field-error" role="alert">
                {fieldErrors.email}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor={`${formId}-password`}>Password</label>
            <input
              type="password"
              id={`${formId}-password`}
              name="password"
              ref={passwordInputRef}
              value={formData.password}
              onChange={handleInputChange}
              className={fieldErrors.password ? 'form-input error' : 'form-input'}
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              aria-required="true"
              aria-invalid={!!fieldErrors.password}
              aria-describedby={fieldErrors.password ? `${formId}-password-error` : undefined}
            />
            {fieldErrors.password && (
              <span id={`${formId}-password-error`} className="field-error" role="alert">
                {fieldErrors.password}
              </span>
            )}
          </div>

          <LoadingButton
            type="submit"
            className="submit-btn"
            loading={isSubmitting}
            loadingText="Signing in..."
            disabled={isSubmitting}
            aria-label="Sign in to your account"
          >
            Sign In
          </LoadingButton>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <Link to={ROUTES.REGISTER} state={location.state}>
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;